// import components
import ControlledTimePicker from 'components/shared/ControlledFields/ControlledTimePicker';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

// import hooks
import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import types
import { ControlledTimeFromTimeToProps } from './ControlledTimeFromTimeTo.types';

// import utils
import dayjs from 'dayjs';
import { normalizeTimeRange, TIME_RANGE_BASE_DATE } from 'utils/date';

export default function ControlledTimeFromTimeTo({
  control,
  timeFromFieldName = 'timeFrom',
  timeToFieldName = 'timeTo',
  timeFromLabel,
  timeToLabel,
  baseDate = TIME_RANGE_BASE_DATE,
  disabled,
  variant = 'outlined'
}: ControlledTimeFromTimeToProps) {
  'use memo';

  const { t } = useTranslation('common');

  // setValue keeps the form clean while correcting a stored range; it needs a
  // FormProvider, so fall back to the field's own onChange without one.
  const setValue = useFormContext()?.setValue;

  const { field: timeFromField } = useController({
    name: timeFromFieldName,
    control
  });
  const { field: timeToField } = useController({
    name: timeToFieldName,
    control
  });

  const timeFrom = timeFromField.value;
  const timeTo = timeToField.value;

  // Normalize on every value change, mount included, so a stored 00:00-00:00
  // range already shows its ⁺¹ before the user touches a picker.
  useEffect(() => {
    // An emptied or half-typed field arrives here as null; leave it alone so
    // the required-validation still fires instead of coercing it to baseDate.
    if (
      !timeFrom ||
      !timeTo ||
      !dayjs(timeFrom).isValid() ||
      !dayjs(timeTo).isValid()
    ) {
      return;
    }

    const normalized = normalizeTimeRange(timeFrom, timeTo, baseDate);

    // Correcting a stored range is not a user edit, so it must not mark the form
    // dirty - otherwise closing an untouched record raises the unsaved-changes
    // dialog. normalizeTimeRange is idempotent but returns fresh Dayjs objects,
    // so only write on a real change; writing unconditionally would loop.
    if (!normalized.timeFrom.isSame(timeFrom)) {
      if (setValue) {
        setValue(timeFromFieldName, normalized.timeFrom, {
          shouldDirty: false
        });
      } else {
        timeFromField.onChange(normalized.timeFrom);
      }
    }
    if (!normalized.timeTo.isSame(timeTo)) {
      if (setValue) {
        setValue(timeToFieldName, normalized.timeTo, { shouldDirty: false });
      } else {
        timeToField.onChange(normalized.timeTo);
      }
    }
  }, [
    timeFrom,
    timeTo,
    baseDate,
    timeFromField,
    timeToField,
    timeFromFieldName,
    timeToFieldName,
    setValue
  ]);

  return (
    <GridContainer>
      <GridCompact6>
        <ControlledTimePicker
          fullWidth
          control={control}
          name={timeFromFieldName}
          label={timeFromLabel ?? t('timeFrom')}
          variant={variant}
          disabled={disabled}
          baseDate={baseDate}
        />
      </GridCompact6>
      <GridCompact6>
        <ControlledTimePicker
          fullWidth
          control={control}
          name={timeToFieldName}
          label={timeToLabel ?? t('timeTo')}
          variant={variant}
          disabled={disabled}
          baseDate={baseDate}
        />
      </GridCompact6>
    </GridContainer>
  );
}
